import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { CurrencyService, ICurrency } from '@portal/shared/helpers/src';
import { ITooltipAttributes } from '@portal/shared/ui/tooltip/src';
import { ICurrencyMaskConfig } from '../../../modules/transactions/interfaces/currency-mask-config.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { MAX_DIGITS_TOTAL } from '../../vt-and-pbl/constants/checkout-max-amount.constant';

@UntilDestroy()
@Component({
  selector: 'portal-currency-amount',
  templateUrl: './currency-amount.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrencyAmountComponent implements OnChanges {
  @Input() amount: UntypedFormControl;
  @Input() currency: UntypedFormControl;
  @Input() canChangeCurrency = true;
  @Input() currencies: ICurrency[];
  @Input() invalid: boolean;
  @Input() id: string;
  @Input() isLightGrey: false;
  @Input() maxLength = 0;
  @Input() expandAmountField = false;
  @Input() isAmountDisabled = false;
  @Input() isCustomValidation = false;
  @Output() currencySelected: EventEmitter<string> = new EventEmitter();
  @Output() amountSelected: EventEmitter<number> = new EventEmitter();

  @Input() amountMask: ICurrencyMaskConfig = {
    align: 'right',
    allowNegative: false,
    prefix: '',
    suffix: '',
    precision: this.getPrecision(),
    thousands: this.currencyService.thousands,
    decimal: this.currencyService.decimal,
  };

  private currencyChanges$ = new Subject();

  get currencyIsSelectable(): boolean {
    return this.currencies?.length > 1 && this.canChangeCurrency;
  }

  constructor(
    private currencyService: CurrencyService,
    private elementRef: ElementRef,
    private ngZone: NgZone,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currency) {
      this.updateAmountMask();
      this.currencyChanges$.next();
      this.currency?.valueChanges
        .pipe(untilDestroyed(this), takeUntil(this.currencyChanges$))
        .subscribe(() => {
          this.updateAmountMask();
        });
    }
  }

  setAutoFocusState(state: boolean): void {
    if (state) {
      this.elementRef?.nativeElement?.querySelector('.input').focus();
    }
  }

  onCurrencySelected(): void {
    this.updateAmountMask();
    this.currencyService.setLocalStorageDefaultCurrencyCode(this.currency.value);
    this.currencySelected.emit(this.currency.value);
  }

  onAmountSelected(): void {
    this.amountSelected.emit(this.amount.value);
  }

  onInputChange(event: Event): void {
    const inputValueWithCommas = (event.target as HTMLInputElement).value;
    const inputValue = parseFloat(inputValueWithCommas.replace(/,/g, ''));
    if (!isNaN(inputValue)) {
      this.amount.setValue(inputValue);
      this.amountSelected.emit(this.amount.value);
    }
  }

  getTooltipTextAttribute(): string {
    const tooltipAttribute: ITooltipAttributes = {
      text: this.currencies?.find((currency) => currency.code === this.currency.value)
        ?.formattedName,
      multiline: false,
      nowrap: true,
    };
    return JSON.stringify(tooltipAttribute);
  }

  /* This function clears format on focus to allow free typing */
  onInputFocus(event: FocusEvent): void {
    const customInputElement = event.target as HTMLInputElement;
    const rawNumber = this.amount.value;
    if (rawNumber === 0) {
      customInputElement.value = '';
    }
  }

  /**
   * This method formats the user's input on blur
   * and ensures the FormControl holds a number and the input displays a formatted string.
   */
  onInputBlurAndFormat(event: FocusEvent): void {
    const customAmountInput = event.target as HTMLInputElement;
    const rawValue = customAmountInput.value;
    const precision = this.getPrecision();

    if (rawValue === null || rawValue === '') {
      this.amount.setValue(0);
      this.amountSelected.emit(0);
      return;
    }
    // Remove all thousands separators and replace the custom decimal with a period for parsing.
    const thousandsSeparator = this.amountMask.thousands;
    const decimalSeparator = this.amountMask.decimal;
    const thousandsRegex = new RegExp(`\\${thousandsSeparator}`, 'g');

    const cleanedValueString = String(rawValue)
      .replace(thousandsRegex, '')
      .replace(decimalSeparator, '.');

    const numberValue = parseFloat(cleanedValueString);
    if (isNaN(numberValue)) {
      this.amount.setValue(0);
      this.amountSelected.emit(0);
      if (customAmountInput) {
        customAmountInput.value = '';
      }
      return;
    }

    const roundedValue = this.currencyService.roundOff(numberValue, this.currency?.value);
    this.amount.setValue(roundedValue, { emitEvent: true });
    this.amountSelected.emit(roundedValue);

    const formattedString = this.formatNumberWithCustomSeparators(roundedValue, precision);
    // Wait until Angular finishes change detection before writing the formatted value.
    // This avoids overwriting the user’s active typing and prevents race conditions when multiple amount inputs exist on the page
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      if (document.activeElement !== customAmountInput && customAmountInput)
        customAmountInput.value = formattedString;
    });
  }

  /**
   * This method limits user to enter max (max_digits - precision) no. of digits before decimal
   * ensuring there is always a max of 12 digits excluding decimal (.) and comma(,)
   */
  onRawInput(e: Event): void {
    const el = e.target as HTMLInputElement;
    const inputVal = el.value;
    const maxInt = MAX_DIGITS_TOTAL - this.getPrecision();

    const rawValue = inputVal.replace(/[^\d.,]/g, '');
    const decimalIndex = rawValue.lastIndexOf('.');
    const intPart = decimalIndex >= 0 ? rawValue.slice(0, decimalIndex) : rawValue;
    const fracPart = decimalIndex >= 0 ? rawValue.slice(decimalIndex + 1) : '';

    const numCommas = (intPart.match(/,/g) || []).length;
    const intDigits = intPart.slice(0, maxInt + numCommas);
    const fracDigits = fracPart.replace(/,/g, '').slice(0, this.getPrecision());

    const finalVal =
      decimalIndex >= 0 ? (fracDigits ? `${intDigits}.${fracDigits}` : `${intDigits}.`) : intDigits;

    el.value = finalVal;
    this.amount.setValue(finalVal, { emitEvent: true });
  }

  private updateAmountMask(): void {
    if (!this.isAmountDisabled) {
      const oldPrecision = this.amountMask.precision;
      const newPrecision = this.getPrecision();
      this.amountMask = {
        ...this.amountMask,
        precision: newPrecision,
      };
      if (newPrecision !== oldPrecision) {
        this.formatAmount();
      }
    }
  }

  private getPrecision(): number {
    return this.currencyService.getCurrencyDigits(this.currency?.value);
  }

  private formatAmount(): void {
    const oldAmount = this.amount?.value;
    if (oldAmount && !this.isAmountDisabled) {
      const newAmount = this.currencyService.roundOff(oldAmount, this.currency?.value);
      if (oldAmount !== newAmount) {
        this.amount.setValue(newAmount);
      }
    }
  }

  private formatNumberWithCustomSeparators(value: number, precision: number): string {
    const decimalSeparator = this.currencyService.decimal;
    const thousandsSeparator = this.currencyService.thousands;

    // Format with US locale to ensure comma = thousands, dot = decimal
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
      useGrouping: true,
    });

    const result = formatter.format(value); // e.g. "16,565,556,656,565.78"

    // Split integer and fractional parts safely
    const [integerPart, fractionalPart] = result.split('.');

    // Replace commas in integer part with custom thousands separator
    const integerWithCustomSep = integerPart.replace(/,/g, thousandsSeparator);

    // Rebuild the final number
    return fractionalPart !== undefined
      ? `${integerWithCustomSep}${decimalSeparator}${fractionalPart}`
      : integerWithCustomSep;
  }
}
